import { type FireAnalyticsEvent } from '../../../common/types';
import { is3pProductKeyForAnalytics } from '../../constants/products';
import type { ConnectorSource } from '../../constants/schemas/3p-config';

import { type CommonAttributes, type NonPrivacySafeAttributes } from './types';

/** Metadata for when the result is interacted with, e.g. clicked or right clicked */
export type AdvancedSearchResultInteractedMetadata = {
	id: string;
	entityType: string;
	contentType?: string | null;
	contentId: string;
	spaceId: string | null;
	containerId: string | null;
	product?: string;
	is3PLink?: boolean;
	is3PSmartLink?: boolean;
	isSideBySideEnabled?: boolean;
	connectorType?: ConnectorSource;
	fromLinksSeeAll?: boolean;
	thirdPartyId?: string;
};

interface ResultInteractedProps {
	metadata: AdvancedSearchResultInteractedMetadata;
	position?: number;
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
	socialSignal?: string;
	linkDocId?: string;
	fromLinksSeeAll?: boolean;
	sublistPosition?: number;
	provider?: string;
	linkDocContentType?: string;
	fanoutProduct?: string;
	fanoutLinkCategory?: string;
	isImpersonated: boolean;
	hasRelatedLinks?: boolean;
	is3PClickIngestionEnabled?: boolean;
	thirdPartyId?: string;
	isAuxClick?: boolean;
	isSummarizeClick?: boolean;
	/** Temporary diagnostic attribute — raw mouse event data from auxclick events. Remove after investigation. */
	tmpAuxDiagnostics?: {
		button: number;
		nativeButton?: number;
		eventType?: string;
		isTrusted?: boolean;
		metaKey?: boolean;
		ctrlKey?: boolean;
	};
}

export const onAdvancedSearchResultLinkClicked = ({
	metadata,
	position,
	commonAttributes,
	nonPrivacySafeAttributes,
	socialSignal,
	fromLinksSeeAll,
	sublistPosition,
	provider,
	linkDocId,
	linkDocContentType,
	fanoutProduct,
	fanoutLinkCategory,
	isImpersonated = false,
	hasRelatedLinks,
	is3PClickIngestionEnabled,
	thirdPartyId,
	isAuxClick = false,
	isSummarizeClick = false,
	tmpAuxDiagnostics,
}: ResultInteractedProps): FireAnalyticsEvent => {
	const { product: metadataProduct, ...restMetadata } = metadata;
	const product = fanoutProduct || metadataProduct;

	return {
		eventType: 'ui',
		action: 'clicked',
		actionSubject: 'advancedSearchResultLink',
		attributes: {
			additionalAttributes: {
				position,
				metadata: {
					...restMetadata,
					product,
					...(fanoutProduct && { parentProduct: metadataProduct }),
					...(fanoutLinkCategory && { fanoutLinkCategory }),
					is3PLink: is3pProductKeyForAnalytics(product),
					fromLinksSeeAll,
					sublistPosition,
					provider,
					linkDocId,
					linkDocContentType,
					hasRelatedLinks,
					thirdPartyId,
				},
				isImpersonated,
				socialSignal,
				is3PClickIngestionEnabled,
				isAuxClick,
				isSummarizeClick,
				...(tmpAuxDiagnostics ? { tmpAuxDiagnostics } : {}),
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
	};
};

export const onAdvancedSearchResultLinkContextMenuClicked = ({
	metadata,
	position,
	commonAttributes,
	nonPrivacySafeAttributes,
	socialSignal,
	sublistPosition,
	linkDocId,
	linkDocContentType,
	fanoutProduct,
	fanoutLinkCategory,
	isImpersonated = false,
	is3PClickIngestionEnabled,
	thirdPartyId,
}: ResultInteractedProps): FireAnalyticsEvent => {
	const { product: metadataProduct, ...restMetadata } = metadata;
	const product = fanoutProduct || metadataProduct;

	return {
		eventType: 'ui',
		action: 'contextMenuClicked',
		actionSubject: 'advancedSearchResultLink',
		attributes: {
			additionalAttributes: {
				position,
				metadata: {
					...restMetadata,
					product,
					...(fanoutProduct && { parentProduct: metadataProduct }),
					...(fanoutLinkCategory && { fanoutLinkCategory }),
					is3PLink: is3pProductKeyForAnalytics(product),
					sublistPosition,
					linkDocId,
					linkDocContentType,
					thirdPartyId,
				},
				isImpersonated,
				socialSignal,
				is3PClickIngestionEnabled,
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
	};
};
